import { formatEmail, normalizePhone } from '../format.js';
import { intakeAnswerValueSchema, validateIntakeAnswers, } from '../intake.js';
import { agencyOwned, matchChoice, YES_NO_OPTIONS } from './choices.js';
/**
 * Answer values: emptiness, equality and validation. Rule (Phases 1 to 5): no legacy value is ever
 * transformed; the canonical value of a legacy answer is its stored JSON. validateAnswer is for NEW
 * writes and returns only today's intake shapes.
 */
/**
 * Mirrors SQL public.talent_is_empty exactly (0094; a PG test pins the two together): null, JSON null,
 * a string of nothing but spaces (Postgres btrim trims spaces only, so a tab or newline is an answer),
 * an empty array, an empty object.
 */
export function isEmptyAnswer(v) {
    if (v === null || v === undefined)
        return true;
    if (typeof v === 'string')
        return /^ *$/.test(v);
    if (Array.isArray(v))
        return v.length === 0;
    if (typeof v === 'object')
        return Object.keys(v).length === 0;
    return false;
}
/**
 * JSON with sorted object keys at every depth: equal strings exactly when Postgres jsonb equality holds.
 * `undefined` (no value) serializes as JSON null, as it would reach the database.
 */
export function canonicalJson(v) {
    return JSON.stringify(sortKeys(v)) ?? 'null';
}
function sortKeys(v) {
    if (Array.isArray(v))
        return v.map(sortKeys);
    if (v === null || typeof v !== 'object')
        return v;
    const out = {};
    for (const k of Object.keys(v).sort()) {
        Object.defineProperty(out, k, { value: sortKeys(v[k]), enumerable: true, writable: true, configurable: true });
    }
    return out;
}
export const answersEqual = (a, b) => canonicalJson(a) === canonicalJson(b);
/**
 * Two answers as an editor compares them to decide what it sends (spec 3.8: only changed keys): two empty answers
 * are the same answer ("nothing" has one meaning, as talent_agency_edit counts it); a phone or an email compares
 * in its stored form, so an input that only reformatted it on blur changed nothing; anything else as stored JSON.
 */
export function sameAnswer(a, b, type) {
    const emptyA = isEmptyAnswer(a);
    const emptyB = isEmptyAnswer(b);
    if (emptyA || emptyB)
        return emptyA && emptyB;
    if (typeof a === 'string' && typeof b === 'string') {
        if (type === 'phone')
            return normalizePhone(a) === normalizePhone(b);
        if (type === 'email')
            return formatEmail(a) === formatEmail(b);
    }
    return answersEqual(a, b);
}
/**
 * A stored profile answer as one line of text: a list joins with commas, a grid reads "Row: Column" per row, a
 * file its name, a phone of a phone list its label and number. Empty is ''. THE reader for every profile value
 * the UI shows (read views, chips, history, submitted forms).
 */
export function answerText(v) {
    if (v == null)
        return '';
    if (typeof v === 'string')
        return v;
    if (Array.isArray(v))
        return v.map((x) => (typeof x === 'object' && x ? answerText(x) : String(x))).join(', ');
    if (typeof v === 'object') {
        const o = v;
        if (typeof o.file_name === 'string')
            return o.file_name;
        if (typeof o.phone === 'string')
            return [o.label, o.phone].filter((s) => typeof s === 'string' && s).join(' ');
        return Object.entries(o).map(([row, col]) => `${row}: ${String(col)}`).join(' · ');
    }
    return String(v);
}
/** The ids among `ids` whose answer in `next` is not the same answer as in `base` (sameAnswer, by each id's type). */
export function changedAnswerIds(base, next, ids, typeOf) {
    return [...new Set(ids)].filter((id) => !sameAnswer(base[id], next[id], typeOf?.(id)));
}
/**
 * The FIXED map from a library question type to the intake field type that collects and stores it
 * (Phase 1 on; Phase 6 changes inputs, never storage): yes_no -> select ['Yes','No']; measurement and
 * url -> short_text; address and contact -> long_text; every other type keeps its name.
 */
export const INTAKE_TYPE_OF_QUESTION = Object.freeze({
    short_text: 'short_text',
    long_text: 'long_text',
    number: 'number',
    measurement: 'short_text',
    date: 'date',
    email: 'email',
    phone: 'phone',
    url: 'short_text',
    address: 'long_text',
    select: 'select',
    multi_select: 'multi_select',
    matrix: 'matrix',
    yes_no: 'select',
    file: 'file',
    contact: 'long_text',
});
/** The storage shape of a library question as an intake field (type, choices, grid): its own rule. */
export function intakeShapeOf(q) {
    const type = INTAKE_TYPE_OF_QUESTION[q.type];
    if (q.type === 'yes_no')
        return { type, options: [...YES_NO_OPTIONS] };
    if (q.type === 'matrix')
        return { type, rows: q.options ?? [], columns: q.columns ?? [] };
    return { type, ...(q.options?.length ? { options: q.options } : {}) };
}
/**
 * Validate one NEW answer to a library (or agency) question. The value comes back as one of today's
 * intake shapes and is never transformed beyond today's intake rules (a number becomes text, as intake
 * stores it). An empty answer is not an answer: callers decide clearing (isEmptyAnswer) before validating.
 * - `asked`: the field the answer was entered through (an agency's own field, or toIntakeField of a way to
 *   ask): exactly today's intake rules for that field, so a library select refuses an unknown choice and an
 *   agency's free-text field takes any text.
 * - no `asked`: whatever some allowed way to ask the question produces (variantAllowed), which is every
 *   legacy shape: a file for a file question; a grid whose rows and columns name the question's; a list of
 *   choices naming its options (a list question only); text by the question's own rule, else as free text
 *   (an agency may ask a choice, a measurement or an address as free text; never a file or a grid). A
 *   question each agency keeps to itself (agencyOwned) takes any list or grid: each agency keeps its own.
 */
export function validateAnswer(q, v, asked) {
    if (isEmptyAnswer(v))
        return noAnswer(q);
    if (asked)
        return byField(q, asked, v);
    if (q.type === 'file')
        return byField(q, { type: 'file' }, v);
    if (q.type === 'matrix')
        return gridAnswer(q, v);
    if (Array.isArray(v))
        return q.type === 'multi_select' ? listAnswer(q, v) : invalid(q);
    if (typeof v === 'object')
        return invalid(q);
    const own = byField(q, intakeShapeOf(q), v);
    return own.ok ? own : byField(q, { type: 'long_text' }, v);
}
const noAnswer = (q) => ({ ok: false, error: `"${q.label}" has no answer` });
const invalid = (q) => ({ ok: false, error: `"${q.label}" has an invalid answer` });
const unknownChoice = (q) => ({ ok: false, error: `"${q.label}" has an unknown choice` });
/** A value as one of today's intake shapes, or the reason it is not one. */
function shaped(q, value) {
    if (value === undefined || isEmptyAnswer(value))
        return noAnswer(q);
    const parsed = intakeAnswerValueSchema.safeParse(value);
    return parsed.success ? { ok: true, value: parsed.data } : invalid(q);
}
/** Today's intake rule for one field (never required here: emptiness is the caller's call). */
function byField(q, field, v) {
    const r = validateIntakeAnswers([{ ...field, id: 'answer', label: field.label ?? q.label, required: false }], { answer: v });
    return r.ok ? shaped(q, r.answers.answer) : r;
}
/** A list question's choices: each names one of its options, unless each agency keeps its own. */
function listAnswer(q, v) {
    if (!v.every((c) => typeof c === 'string'))
        return invalid(q);
    if (!agencyOwned(q) && v.some((c) => matchChoice(q.options ?? [], c, q.option_aliases) === null))
        return unknownChoice(q);
    return shaped(q, v);
}
/** A grid: row -> column, each naming the question's own (unless each agency keeps its own grid). Kept
 *  whole: a cell the question cannot hold refuses the answer instead of silently dropping it. */
function gridAnswer(q, v) {
    if (!v || typeof v !== 'object' || Array.isArray(v))
        return invalid(q);
    const cells = Object.entries(v);
    if (!cells.every(([, c]) => typeof c === 'string'))
        return invalid(q);
    const known = ([row, col]) => matchChoice(q.options ?? [], row, q.option_aliases) !== null && matchChoice(q.columns ?? [], col) !== null;
    if (!agencyOwned(q) && !cells.every(known))
        return unknownChoice(q);
    return shaped(q, v);
}
//# sourceMappingURL=values.js.map